import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Container,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface LoginProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  // Handle magic link token from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkLogin(token);
    }
  }, [searchParams]);

  const handleMagicLinkLogin = async (token: string) => {
    setMagicLinkLoading(true);
    setError('');
    
    try {
      const response = await apiService.verifyMagicLink(token);
      
      if (response.success && response.token) {
        // Store the access token
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('username', response.username || '');
        
        // Redirect to main app
        window.location.href = '/lineups';
      } else {
        setError(response.message || 'Magic link is invalid or expired');
        // Clear the token from URL
        navigate('/login', { replace: true });
      }
    } catch (err) {
      setError('Failed to verify magic link. Please try again or use password login.');
      navigate('/login', { replace: true });
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const success = await onLogin(username, password);
      
      if (!success) {
        setError('Invalid username or password');
        setPassword('');
      }
    } catch (err) {
      setError('Login failed. Please check your connection and try again.');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  // Show magic link verification screen
  if (magicLinkLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f7f9fc 0%, #eceff5 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main', mb: 3 }} />
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Verifying your access...
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we log you in
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f7f9fc 0%, #eceff5 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle branded background accents */}
      <Box
        sx={{
          position: 'absolute',
          top: -140,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: alpha(theme.palette.secondary.main, 0.05),
          animation: 'float 8s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-18px)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -160,
          left: -140,
          width: 480,
          height: 480,
          borderRadius: '50%',
          background: alpha(theme.palette.primary.main, 0.05),
          animation: 'float 10s ease-in-out infinite',
        }}
      />

      <Container maxWidth="sm">
        <Card
          elevation={0}
          sx={{
            borderRadius: '20px',
            overflow: 'hidden',
            background: '#ffffff',
            border: '1px solid',
            borderColor: alpha(theme.palette.secondary.main, 0.1),
            boxShadow: '0 24px 60px rgba(30,42,90,0.14)',
          }}
        >
          {/* Header with branding */}
          <Box
            sx={{
              pt: 4.5,
              pb: 2,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 84,
                height: 84,
                borderRadius: '50%',
                background: alpha(theme.palette.primary.main, 0.08),
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.16),
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <Box
                component="img"
                src="/static/metalogo/rr-crest.png"
                alt="Rajasthan Royals"
                sx={{ height: '66%', width: 'auto', maxWidth: '74%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: 'secondary.main', letterSpacing: '-0.5px' }}>
              RR SquadLab
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Rajasthan Royals Analytics Platform
            </Typography>
          </Box>

          <CardContent sx={{ px: 4, pb: 4, pt: 1 }}>
            <Typography
              variant="h5"
              align="center"
              gutterBottom
              sx={{ mb: 3, fontWeight: 600 }}
            >
              Welcome Back
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px #ffffff inset',
                      WebkitTextFillColor: theme.palette.text.primary,
                      caretColor: theme.palette.text.primary,
                      transition: 'background-color 9999s ease-in-out 0s',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px #ffffff inset',
                      WebkitTextFillColor: theme.palette.text.primary,
                      caretColor: theme.palette.text.primary,
                      transition: 'background-color 9999s ease-in-out 0s',
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !username || !password}
                  startIcon={<LoginIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Stack>
            </form>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Halla Bol! 🏏👑
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.85 }}>
            © 2025 Rajasthan Royals. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;

